import { z } from '@tasama/utils';
import type { FormSectionSchema, SchemaRule } from '@tasama/types';
import {
  buildDynamicFormSchema,
  withDynamicRequiredValidation,
} from '../../my-services/schemas/dynamic-activity-request.schema';
import { TopicCategory } from '../types';

// Base shape only — no superRefine yet, so `getNewTicketFormSchema` can `.merge()` the
// TDF-1390 dynamic (ServiceNow schema-driven) fields onto it before layering validation.
// NOTE: TDF-1388's subTopic/issueKey are deliberately NOT RHF/zod fields, for the same
// reason srAttemptId (Active/Requested Services) isn't one either — they're tracked as
// plain component state in NewTicketDrawer (selectedSubTopic/selectedIssueKey) so the
// Sub-Topic/Issue selection can drive the dynamic schema fetch without a circular
// dependency on the form's own resolver. isFormValid enforces AC6 manually, same as the
// existing srAttemptId requirement for Active/Requested Services.
const newTicketFormObjectSchema = z.object({
  topic: z.nativeEnum(TopicCategory),
  subject: z.string().min(1, 'Required'),
  description: z.string().min(1, 'Required'),
  functionL1: z.string(),
  typeL2: z.string(),
  serviceL3: z.string(),
});

function withTopicRequiredValidation<T extends z.ZodTypeAny>(schema: T) {
  return schema.superRefine((data, ctx) => {
    const values = data as z.infer<typeof newTicketFormObjectSchema>;
    if (values.topic !== TopicCategory.SERVICE_SPECIFIC) return;

    if (!values.functionL1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Required',
        path: ['functionL1'],
      });
    }
    if (!values.typeL2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Required',
        path: ['typeL2'],
      });
    }
    if (!values.serviceL3) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Required',
        path: ['serviceL3'],
      });
    }
  });
}

export const newTicketFormSchema = withTopicRequiredValidation(
  newTicketFormObjectSchema
);

export type NewTicketFormValues = z.infer<typeof newTicketFormObjectSchema>;

export const newTicketFormDefaultValues: NewTicketFormValues = {
  topic: TopicCategory.GENERAL,
  subject: '',
  description: '',
  functionL1: '',
  typeL2: '',
  serviceL3: '',
};

/**
 * TDF-1390: reuses the same dynamic-form schema builder as Activity Requests
 * (dynamic-activity-request.schema.ts) so the issue's ServiceNow-driven fields — once an
 * Oracle ERP Issue or the single IT Services item is selected — get the same mandatory/
 * conditional validation as the rest of the app, on top of the static Topic checks above.
 * Called fresh on every render (dynamicSections/dynamicRules come from the schema query),
 * mirroring ActivityRequestFormContainer's "recreate schema each render so the resolver
 * ref stays current" pattern.
 */
export function getNewTicketFormSchema(
  dynamicSections: FormSectionSchema[],
  dynamicRules: SchemaRule[],
  t: (_key: string) => string
) {
  const merged = newTicketFormObjectSchema.merge(
    buildDynamicFormSchema(dynamicSections, t)
  );
  const withDynamicRequired = withDynamicRequiredValidation(
    merged,
    dynamicSections,
    dynamicRules,
    t
  );
  return withTopicRequiredValidation(withDynamicRequired);
}
